import { useMemo } from 'react';
import { FiFilter, FiX } from 'react-icons/fi';
import type { ExpenseCategory } from '@/lib/types';
import { Button } from '@/components';

export type ExpenseFilterState = {
  selectedCategories: string[];
  minAmount: string;
  maxAmount: string;
  filterStartDate: string;
  filterEndDate: string;
  paidFilter: 'all' | 'paid' | 'unpaid';
};

export const EMPTY_EXPENSE_FILTERS: ExpenseFilterState = {
  selectedCategories: [],
  minAmount: '',
  maxAmount: '',
  filterStartDate: '',
  filterEndDate: '',
  paidFilter: 'all',
};

export function buildExpenseFilterQuery(filters: ExpenseFilterState): string {
  const params = new URLSearchParams();
  if (filters.selectedCategories.length > 0) {
    params.set('category', filters.selectedCategories.join(','));
  }
  if (filters.minAmount) {
    const cents = Math.round(parseFloat(filters.minAmount) * 100);
    if (!Number.isNaN(cents)) params.set('min_amount_cents', String(cents));
  }
  if (filters.maxAmount) {
    const cents = Math.round(parseFloat(filters.maxAmount) * 100);
    if (!Number.isNaN(cents)) params.set('max_amount_cents', String(cents));
  }
  if (filters.filterStartDate) params.set('start_date', filters.filterStartDate);
  if (filters.filterEndDate) params.set('end_date', filters.filterEndDate);
  if (filters.paidFilter === 'paid') params.set('is_paid', 'true');
  else if (filters.paidFilter === 'unpaid') params.set('is_paid', 'false');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function countActiveExpenseFilters(filters: ExpenseFilterState): number {
  let count = 0;
  if (filters.selectedCategories.length > 0) count++;
  if (filters.minAmount || filters.maxAmount) count++;
  if (filters.filterStartDate || filters.filterEndDate) count++;
  if (filters.paidFilter !== 'all') count++;
  return count;
}

type ExpenseFilterModalProps = {
  open: boolean;
  onClose: () => void;
  filters: ExpenseFilterState;
  onChange: (filters: ExpenseFilterState) => void;
  categories: ExpenseCategory[];
};

export function ExpenseFilterModal({
  open,
  onClose,
  filters,
  onChange,
  categories,
}: ExpenseFilterModalProps) {
  const activeCount = useMemo(() => countActiveExpenseFilters(filters), [filters]);

  if (!open) return null;

  const toggleCategory = (categoryId: string) => {
    const selected = filters.selectedCategories.includes(categoryId)
      ? filters.selectedCategories.filter((c) => c !== categoryId)
      : [...filters.selectedCategories, categoryId];
    onChange({ ...filters, selectedCategories: selected });
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto'>
        <div className='flex justify-between items-center mb-4'>
          <h2 className='text-xl font-bold text-gray-900 flex items-center gap-2'>
            <FiFilter className='w-5 h-5 text-primary' />
            Filter expenses
          </h2>
          <button type='button' onClick={onClose} className='p-2 hover:bg-gray-100 rounded-full'>
            <FiX className='w-5 h-5' />
          </button>
        </div>

        <div className='space-y-5'>
          <div>
            <p className='text-sm font-semibold text-gray-600 mb-2'>Category</p>
            <div className='flex flex-wrap gap-2'>
              {categories.map((cat) => {
                const selected = filters.selectedCategories.includes(cat.expense_category_id);
                return (
                  <button
                    key={cat.expense_category_id}
                    type='button'
                    onClick={() => toggleCategory(cat.expense_category_id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                      selected
                        ? 'bg-primary text-white border-primary'
                        : 'bg-gray-50 text-gray-700 border-gray-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='block text-sm font-semibold text-gray-600 mb-1'>Min amount</label>
              <input
                type='number'
                min='0'
                step='0.01'
                value={filters.minAmount}
                onChange={(e) => onChange({ ...filters, minAmount: e.target.value })}
                placeholder='0.00'
                className='w-full px-3 py-2.5 rounded-xl border border-gray-200'
              />
            </div>
            <div>
              <label className='block text-sm font-semibold text-gray-600 mb-1'>Max amount</label>
              <input
                type='number'
                min='0'
                step='0.01'
                value={filters.maxAmount}
                onChange={(e) => onChange({ ...filters, maxAmount: e.target.value })}
                placeholder='0.00'
                className='w-full px-3 py-2.5 rounded-xl border border-gray-200'
              />
            </div>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='block text-sm font-semibold text-gray-600 mb-1'>From</label>
              <input
                type='date'
                value={filters.filterStartDate}
                onChange={(e) => onChange({ ...filters, filterStartDate: e.target.value })}
                className='w-full px-3 py-2.5 rounded-xl border border-gray-200'
              />
            </div>
            <div>
              <label className='block text-sm font-semibold text-gray-600 mb-1'>To</label>
              <input
                type='date'
                value={filters.filterEndDate}
                onChange={(e) => onChange({ ...filters, filterEndDate: e.target.value })}
                className='w-full px-3 py-2.5 rounded-xl border border-gray-200'
              />
            </div>
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-600 mb-2'>Paid status</p>
            <div className='flex rounded-xl border border-gray-200 overflow-hidden'>
              {(['all', 'paid', 'unpaid'] as const).map((opt) => (
                <button
                  key={opt}
                  type='button'
                  onClick={() => onChange({ ...filters, paidFilter: opt })}
                  className={`flex-1 py-2 text-sm font-semibold capitalize ${
                    filters.paidFilter === opt
                      ? 'bg-primary text-white'
                      : 'bg-gray-50 text-gray-600'
                  }`}
                >
                  {opt === 'all' ? 'All' : opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className='flex gap-3 mt-6'>
          {activeCount > 0 && (
            <Button
              variant='secondary'
              fullWidth
              onClick={() => onChange(EMPTY_EXPENSE_FILTERS)}
            >
              Clear
            </Button>
          )}
          <Button fullWidth onClick={onClose}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
